import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../../lib/auth";
import { toast } from "sonner";
import {
  Brain, RefreshCw, Sparkles, MessageSquare, Send, Loader2, TrendingUp, AlertCircle,
  CheckCircle2, Info, Lightbulb, X, ChevronRight,
} from "lucide-react";

const SEVERITY_STYLES = {
  positive: { bg: "bg-emerald-50 border-emerald-200", icon: <CheckCircle2 size={16} className="text-emerald-600" />, lbl: "Positive" },
  warning:  { bg: "bg-amber-50 border-amber-200",     icon: <AlertCircle size={16} className="text-amber-600" />,    lbl: "Warning" },
  critical: { bg: "bg-red-50 border-red-200",          icon: <AlertCircle size={16} className="text-red-600" />,     lbl: "Critical" },
  neutral:  { bg: "bg-sky-50 border-sky-200",          icon: <Info size={16} className="text-sky-600" />,           lbl: "Insight" },
};

const CATEGORY_LABEL = {
  sales: "Sales", inventory: "Inventory", crm: "CRM", customers: "Customers", cashflow: "Cashflow",
};

const ngn = (n) => `₦${Number(n || 0).toLocaleString()}`;

export default function AIAnalyticsPage() {
  const { subdomain } = useParams();
  const [planLocked, setPlanLocked] = useState(false);
  const [insights, setInsights] = useState([]);
  const [ctx, setCtx] = useState(null);
  const [model, setModel] = useState("");
  const [generating, setGenerating] = useState(false);
  const [period, setPeriod] = useState(30);
  const [tab, setTab] = useState("insights");

  // Auto-load latest snapshot once
  useEffect(() => {
    api.get("/t/ai-analytics/insights/history").then((r) => {
      if (r.data?.[0]) {
        setInsights(r.data[0].insights || []);
        setCtx(r.data[0].context_snapshot || null);
        setModel(r.data[0].model || "");
      }
    }).catch((e) => {
      if (e?.response?.status === 403) setPlanLocked(true);
    });
    api.get("/t/ai-analytics/context").then((r) => setCtx((prev) => prev || r.data)).catch(() => {});
  }, []);

  const generate = async () => {
    setGenerating(true);
    try {
      const r = await api.post("/t/ai-analytics/insights", { period_days: period });
      setInsights(r.data.insights || []);
      setCtx(r.data.context || null);
      setModel(r.data.model || "");
      toast.success(`${r.data.insights?.length || 0} insights generated`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI service error");
    } finally { setGenerating(false); }
  };

  if (planLocked) {
    return (
      <div className="p-8" data-testid="ai-plan-locked">
        <div className="max-w-2xl mx-auto bg-amber-50 border border-amber-200 rounded-2xl p-6 text-center">
          <Brain size={32} className="mx-auto text-amber-700 mb-3" />
          <div className="font-display font-extrabold text-xl text-amber-900">AI Analytics is not in your plan</div>
          <Link to={`/t/${subdomain}/billing`} className="inline-block mt-4 px-5 py-2.5 rounded-xl bg-[#1FA868] text-white font-bold text-sm">View plans</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8" data-testid="ai-analytics-page">
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-violet-600 to-fuchsia-500 flex items-center justify-center text-white shadow-lg shadow-violet-600/30">
            <Brain size={26} />
          </div>
          <div>
            <h1 className="font-display font-extrabold text-2xl lg:text-3xl text-black">AI Business Analyst</h1>
            <p className="text-sm text-black/55 mt-1 flex items-center gap-2">
              <Sparkles size={12} /> Powered by Gemini 2.5 Flash · Insights from your live data
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select value={period} onChange={(e) => setPeriod(parseInt(e.target.value, 10))}
            className="px-3 py-2.5 rounded-lg border border-black/15 text-sm font-semibold" data-testid="ai-period">
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          <button onClick={generate} disabled={generating}
            className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-violet-600 to-fuchsia-500 text-white text-sm font-bold flex items-center gap-2 disabled:opacity-50 shadow-md shadow-violet-600/30"
            data-testid="ai-generate-btn">
            {generating ? <><Loader2 className="animate-spin" size={14} /> Analyzing…</> : <><Sparkles size={14} /> Generate insights</>}
          </button>
        </div>
      </div>

      <div className="flex items-center gap-1 mb-6 border-b border-black/8">
        {[{ key: "insights", lbl: "Insights", icon: Lightbulb }, { key: "chat", lbl: "Ask AI", icon: MessageSquare }].map((t) => {
          const Icon = t.icon;
          return (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`ai-tab-${t.key}`}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-bold border-b-2 ${tab === t.key ? "border-violet-600 text-violet-700" : "border-transparent text-black/55 hover:text-black"}`}>
              <Icon size={15} /> {t.lbl}
            </button>
          );
        })}
      </div>

      {tab === "insights" ? (
        <InsightsView insights={insights} ctx={ctx} model={model} generating={generating} onGenerate={generate} />
      ) : (
        <ChatView />
      )}
    </div>
  );
}

function InsightsView({ insights, ctx, model, generating, onGenerate }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="ai-insights-view">
      <div className="lg:col-span-2 space-y-3">
        {generating ? (
          <div className="bg-white rounded-2xl border border-black/8 p-12 text-center" data-testid="ai-loading">
            <Loader2 className="animate-spin mx-auto text-violet-600" size={28} />
            <div className="mt-3 font-bold text-black">Analyzing your last {ctx?.period_days || 30} days…</div>
            <div className="text-sm text-black/50 mt-1">Aggregating sales, inventory, CRM and inbox data</div>
          </div>
        ) : insights.length === 0 ? (
          <div className="bg-white rounded-2xl border border-black/8 p-12 text-center" data-testid="ai-empty">
            <Brain size={36} className="mx-auto text-black/25 mb-3" />
            <div className="font-bold text-black/70">No insights yet</div>
            <p className="text-sm text-black/50 mt-1 max-w-md mx-auto">Click <strong>Generate insights</strong> to have AI scan your business data and surface what to act on.</p>
            <button onClick={onGenerate} className="mt-4 px-5 py-2.5 rounded-xl bg-gradient-to-r from-violet-600 to-fuchsia-500 text-white text-sm font-bold">Generate first report</button>
          </div>
        ) : (
          insights.map((ins, i) => {
            const style = SEVERITY_STYLES[ins.severity] || SEVERITY_STYLES.neutral;
            return (
              <div key={i} className={`rounded-2xl border p-5 ${style.bg}`} data-testid={`ai-insight-${i}`}>
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-white/70 flex items-center justify-center">{style.icon}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="text-[10px] uppercase tracking-wide font-bold text-black/60">{style.lbl}</span>
                      {ins.category && <><span className="text-black/30">•</span><span className="text-[10px] uppercase tracking-wide font-bold text-black/55">{CATEGORY_LABEL[ins.category] || ins.category}</span></>}
                    </div>
                    <div className="font-display font-extrabold text-lg text-black leading-tight">{ins.title}</div>
                    <p className="mt-1.5 text-sm text-black/75 leading-relaxed">{ins.summary}</p>
                    {ins.action && (
                      <div className="mt-3 flex items-start gap-2 bg-white/80 rounded-lg px-3 py-2 text-sm">
                        <ChevronRight size={14} className="text-violet-600 flex-shrink-0 mt-0.5" />
                        <span className="font-semibold text-black/85"><span className="text-violet-700">Action:</span> {ins.action}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Sidebar: live business context */}
      <div className="lg:col-span-1">
        <div className="bg-white rounded-2xl border border-black/8 p-5 sticky top-4">
          <div className="font-display font-extrabold text-base text-black mb-3 flex items-center gap-2">
            <TrendingUp size={16} className="text-violet-600" /> Live Business Context
          </div>
          {ctx ? (
            <div className="space-y-2.5 text-sm">
              <KV lbl="Total sales" v={ngn(ctx.kpis?.total_sales)} />
              <KV lbl="Sales count" v={ctx.kpis?.sales_count} />
              <KV lbl="Avg. order" v={ngn(ctx.kpis?.average_order_value)} />
              <Divider />
              <KV lbl="Customers" v={ctx.kpis?.customers_total} />
              <KV lbl="Open leads" v={ctx.kpis?.open_leads} />
              <KV lbl="Open deals" v={ctx.kpis?.open_deals} />
              <KV lbl="Pipeline value" v={ngn(ctx.kpis?.pipeline_value)} />
              <KV lbl="Won value" v={ngn(ctx.kpis?.won_value)} />
              <KV lbl="Inbox conversations" v={ctx.kpis?.open_conversations} />
              {ctx.low_stock_alerts?.length > 0 && (
                <>
                  <Divider />
                  <div className="text-[11px] uppercase font-bold text-red-700">Low stock</div>
                  {ctx.low_stock_alerts.slice(0, 5).map((p, i) => (
                    <div key={i} className="flex items-center justify-between text-xs">
                      <span className="text-black/75 truncate">{p.name}</span>
                      <span className="font-bold text-red-700">{p.stock} left</span>
                    </div>
                  ))}
                </>
              )}
            </div>
          ) : <div className="text-sm text-black/45">Loading…</div>}
          {model && <div className="mt-4 pt-3 border-t border-black/8 text-[10px] text-black/40 text-center">Model: {model}</div>}
        </div>
      </div>
    </div>
  );
}

function ChatView() {
  const [sessions, setSessions] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  const loadSessions = () => api.get("/t/ai-analytics/chat/sessions").then((r) => setSessions(r.data || []));
  const loadMessages = (sid) => api.get(`/t/ai-analytics/chat/${sid}`).then((r) => setMessages(r.data || []));

  useEffect(() => { loadSessions(); }, []);
  useEffect(() => { active && loadMessages(active); }, [active]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async () => {
    const text = draft.trim(); if (!text || sending) return;
    setSending(true);
    setMessages((m) => [...m, { role: "user", content: text, created_at: new Date().toISOString() }]);
    setDraft("");
    try {
      const r = await api.post("/t/ai-analytics/chat", { message: text, session_id: active });
      setMessages((m) => [...m, { role: "assistant", content: r.data.reply, created_at: new Date().toISOString() }]);
      if (!active) { setActive(r.data.session_id); }
      loadSessions();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI service error");
      setMessages((m) => m.filter((_, i) => i !== m.length - 1));
    } finally { setSending(false); }
  };

  const removeSession = async (sid) => {
    if (!window.confirm("Delete this chat?")) return;
    await api.delete(`/t/ai-analytics/chat/${sid}`);
    if (active === sid) { setActive(null); setMessages([]); }
    loadSessions();
  };

  const newChat = () => { setActive(null); setMessages([]); };

  const suggestions = [
    "What's my best-selling product this month?",
    "Which leads should I follow up with first?",
    "What products are running low on stock?",
    "How does this month compare to last month?",
  ];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 h-[calc(100vh-260px)] min-h-[500px]" data-testid="ai-chat-view">
      <aside className="lg:col-span-1 bg-white rounded-2xl border border-black/8 p-3 overflow-hidden flex flex-col">
        <button onClick={newChat} className="w-full mb-3 px-3 py-2 rounded-lg bg-gradient-to-r from-violet-600 to-fuchsia-500 text-white text-sm font-bold flex items-center justify-center gap-2" data-testid="ai-new-chat-btn">
          <MessageSquare size={14} /> New chat
        </button>
        <div className="text-[10px] uppercase font-bold text-black/45 px-1 mb-2">Recent</div>
        <div className="flex-1 overflow-y-auto space-y-1">
          {sessions.length === 0 ? (
            <div className="text-xs text-black/35 text-center py-6">No chats yet</div>
          ) : sessions.map((s) => (
            <div key={s.session_id} onClick={() => setActive(s.session_id)} data-testid={`ai-chat-session-${s.session_id}`}
              className={`group p-2 rounded-lg cursor-pointer flex items-start gap-2 text-xs ${active === s.session_id ? "bg-violet-50" : "hover:bg-black/[0.03]"}`}>
              <MessageSquare size={12} className="text-violet-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-bold text-black/85 truncate">{s.last_message || "Empty"}</div>
                <div className="text-[10px] text-black/45 mt-0.5">{s.message_count} msgs</div>
              </div>
              <button onClick={(e) => { e.stopPropagation(); removeSession(s.session_id); }} className="opacity-0 group-hover:opacity-100 text-black/35 hover:text-red-600"><X size={11} /></button>
            </div>
          ))}
        </div>
      </aside>

      <main className="lg:col-span-3 bg-white rounded-2xl border border-black/8 flex flex-col overflow-hidden">
        <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="ai-chat-body">
          {messages.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center px-8 py-12">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-violet-600 to-fuchsia-500 flex items-center justify-center text-white mb-4 shadow-lg shadow-violet-600/30"><Brain size={26} /></div>
              <div className="font-display font-extrabold text-xl text-black">Ask anything about your business</div>
              <p className="text-sm text-black/55 mt-1 max-w-md">I have access to your sales, inventory, CRM, and inbox data. Try one of these to get started:</p>
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-2 max-w-2xl">
                {suggestions.map((s) => (
                  <button key={s} onClick={() => setDraft(s)} className="text-left text-sm px-4 py-2.5 rounded-xl bg-black/[0.04] hover:bg-violet-50 hover:text-violet-700 transition" data-testid={`ai-suggestion-${s.slice(0, 10)}`}>{s}</button>
                ))}
              </div>
            </div>
          ) : (
            messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`ai-msg-${i}`}>
                <div className={`max-w-[78%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-violet-600 text-white rounded-br-md" : "bg-black/[0.04] text-black rounded-bl-md"}`}>
                  {m.content}
                </div>
              </div>
            ))
          )}
          {sending && (
            <div className="flex justify-start">
              <div className="bg-black/[0.04] rounded-2xl rounded-bl-md px-4 py-2.5 text-sm text-black/55 flex items-center gap-2"><Loader2 className="animate-spin" size={12} /> Thinking…</div>
            </div>
          )}
          <div ref={endRef} />
        </div>
        <div className="p-3 border-t border-black/8">
          <div className="flex items-end gap-2 rounded-xl border border-black/15 px-3 py-2">
            <textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={1}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Ask anything about your business…"
              className="flex-1 resize-none focus:outline-none text-sm max-h-32"
              data-testid="ai-chat-input" />
            <button onClick={send} disabled={!draft.trim() || sending} className="p-2 rounded-lg bg-violet-600 disabled:opacity-40 text-white" data-testid="ai-chat-send">
              {sending ? <Loader2 className="animate-spin" size={15} /> : <Send size={15} />}
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}

function KV({ lbl, v }) {
  return <div className="flex items-center justify-between"><span className="text-black/55 text-xs">{lbl}</span><span className="font-bold text-black tabular-nums">{v ?? "—"}</span></div>;
}
function Divider() { return <div className="border-t border-black/8 my-2" />; }
